import type {
  JSentinelOptions,
  JSentinelMessage,
  ScriptOkDetail,
  ScriptMismatchDetail,
  ScriptDetailBase,
} from "./types";

/** ---------- helpers ---------- **/

const enc = new TextEncoder();
async function sha256(str: string) {
  const buf = await crypto.subtle.digest("SHA-256", enc.encode(str));
  return "sha256-" + btoa(String.fromCharCode(...new Uint8Array(buf)));
}

function emit(message: JSentinelMessage) {
  window.dispatchEvent(
    new CustomEvent("jsentinel", {
      detail: message,
      bubbles: true,
      composed: true,
    })
  );
}

export function onJSentinel<T extends JSentinelMessage["type"]>(
  type: T,
  handler: (data: Extract<JSentinelMessage, { type: T }>["data"]) => void
) {
  const h = (e: Event) => {
    const ce = e as CustomEvent<JSentinelMessage>;
    if (ce.detail?.type === type) handler(ce.detail.data as any);
  };
  window.addEventListener("jsentinel", h as EventListener);
  return h;
}
export function onJSentinelAny(handler: (msg: JSentinelMessage) => void) {
  const h = (e: Event) => handler((e as CustomEvent<JSentinelMessage>).detail);
  window.addEventListener("jsentinel", h as EventListener);
  return h;
}
export function offJSentinel(listener: EventListener) {
  window.removeEventListener("jsentinel", listener);
}

/** inline normalize + volatile detection (CLI ile eşleşir) */
function normalizeInline(code: string) {
  return code
    .trim()
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+/g, " ")
    .replace(/[ \t]*\n[ \t]*/g, "\n");
}
function isVolatileInline(node: HTMLScriptElement, raw: string) {
  const t = (node.type || "").toLowerCase();
  if (t === "application/ld+json") return true;
  if (/__NEXT_DATA__|self\.__next_|window\.__NUXT__/.test(raw)) return true;
  if (/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/.test(raw)) return true;
  return false;
}

/** manifest model (CLI'nin yeni şemasını runtime'a indirgeme) */
type ManifestEntry =
  | { src: string; type: "same-origin"; hash?: string; volatile?: boolean }
  | { src: string; type: "versioned-cdn"; hash?: string; volatile?: boolean }
  | {
      src: string;
      type: "dynamic-loader";
      host?: string;
      path?: string;
      query?: string;
      volatile?: boolean;
    }
  | { inline: string; type: "inline-stable"; hash: string; volatile?: false }
  | { inline: string; type: "inline-volatile"; volatile: true };

type RuntimeManifest = {
  sameOriginHashes: Map<string, string>;
  inlineHashes: Set<string>;
  versionedPins: Set<string>;
  dynamicPins: Array<{ host?: string; path?: string; query?: string }>;
  filenameIndex: Map<string, string>; // dosya adına göre fallback eşleşme
};

function buildRuntimeManifest(entries: ManifestEntry[]): RuntimeManifest {
  const sameOriginHashes = new Map<string, string>();
  const inlineHashes = new Set<string>();
  const versionedPins = new Set<string>();
  const dynamicPins: Array<{ host?: string; path?: string; query?: string }> =
    [];
  const filenameIndex = new Map<string, string>();

  for (const e of entries) {
    if ("inline" in e) {
      if (e.type === "inline-stable" && e.hash) inlineHashes.add(e.hash);
      continue;
    }
    if (e.type === "same-origin" && e.hash) {
      try {
        const u = new URL(e.src, window.location.href);
        sameOriginHashes.set(u.pathname, e.hash);
        sameOriginHashes.set(decodeURIComponent(u.pathname), e.hash);
        sameOriginHashes.set(e.src, e.hash);
        const fn = (u.pathname.split("/").pop() || "").toLowerCase();
        if (fn) filenameIndex.set(fn, e.hash);
      } catch {
        sameOriginHashes.set(e.src, e.hash);
        const fn = (e.src.split("/").pop() || "").toLowerCase();
        if (fn) filenameIndex.set(fn, e.hash);
      }
    } else if (e.type === "versioned-cdn") {
      versionedPins.add(e.src);
    } else if (e.type === "dynamic-loader") {
      dynamicPins.push({ host: e.host, path: e.path, query: e.query });
    }
  }
  return {
    sameOriginHashes,
    inlineHashes,
    versionedPins,
    dynamicPins,
    filenameIndex,
  };
}

/** verbose telemetry */
function emitCheckedVerbose(
  enabled: boolean,
  payload: ScriptDetailBase & { ok: boolean }
) {
  if (!enabled) return;
  window.dispatchEvent(
    new CustomEvent("jsentinel", {
      detail: { type: "script.checked", data: payload } as any,
      bubbles: true,
      composed: true,
    })
  );
}

/** url pin kontrolü (dynamic/versioned) */
function urlMatchesPin(src: string, pins: RuntimeManifest["dynamicPins"]) {
  try {
    const u = new URL(src, window.location.href);
    return pins.some((p) => {
      if (p.host && u.host !== p.host) return false;
      if (p.path && u.pathname !== p.path) return false;
      if (p.query && u.search.replace(/^\?/, "") !== (p.query || ""))
        return false;
      return true;
    });
  } catch {
    return false;
  }
}

/** ---------- core check ---------- **/
async function check(
  node: HTMLScriptElement,
  man: RuntimeManifest,
  verbose: boolean
) {
  try {
    // 1) INLINE
    if (!node.src) {
      const raw = node.textContent || "";
      const norm = normalizeInline(raw);
      const got = await sha256(norm);
      const ok = man.inlineHashes.has(got);

      emitCheckedVerbose(verbose, {
        key: "inline",
        src: null,
        inline: true,
        expected: ok ? got : undefined,
        got,
        ok,
      });

      if (ok) {
        const data: ScriptOkDetail = {
          key: "inline",
          src: null,
          inline: true,
          expected: got,
          got,
          ok: true,
          severity: "info",
        };
        emit({ type: "script.ok", data });
        return true;
      }

      const volatile = isVolatileInline(node, raw);
      const data: ScriptMismatchDetail = {
        key: "inline",
        src: null,
        inline: true,
        expected: undefined,
        got,
        ok: false,
        severity: volatile ? "warn" : "error",
        reason: volatile ? "volatile-inline" : "manifest-missing-key",
      };
      emit({ type: "script.mismatch", data });
      return false;
    }

    // 2) SRC’Lİ
    const src = node.src;
    let sameOrigin = false;
    let pathname = "";
    try {
      const u = new URL(src, window.location.href);
      sameOrigin = u.origin === window.location.origin;
      pathname = u.pathname;
    } catch {}

    // 2a) same-origin → enforce (hash zorunlu)
    if (sameOrigin) {
      const expectedDirect =
        man.sameOriginHashes.get(pathname) ||
        man.sameOriginHashes.get(decodeURIComponent(pathname)) ||
        man.sameOriginHashes.get(src);

      let expected = expectedDirect;
      if (!expected) {
        const fn = (pathname.split("/").pop() || "").toLowerCase();
        if (fn) {
          const byFile = man.filenameIndex.get(fn);
          if (byFile) expected = byFile; // filename fallback
        }
      }

      let got = "";
      let fetchFailed = false;
      try {
        const code = await (await fetch(src, { cache: "no-store" })).text();
        got = await sha256(code);
      } catch {
        fetchFailed = true;
      }

      if (fetchFailed) {
        const data: ScriptMismatchDetail = {
          key: pathname || src,
          src,
          inline: false,
          expected,
          got: "",
          ok: false,
          severity: "error",
          reason: "fetch-error",
        };
        emit({ type: "script.mismatch", data });
        return false;
      }

      const ok = expected === got;
      emitCheckedVerbose(verbose, {
        key: pathname || src,
        src,
        inline: false,
        expected,
        got,
        ok,
      });

      if (ok) {
        const data: ScriptOkDetail = {
          key: pathname || src,
          src,
          inline: false,
          expected,
          got,
          ok: true,
          severity: "info",
        };
        emit({ type: "script.ok", data });
        return true;
      } else {
        const data: ScriptMismatchDetail = {
          key: pathname || src,
          src,
          inline: false,
          expected,
          got,
          ok: false,
          severity: "error",
          reason: expected ? "hash-different" : "manifest-missing-key",
        };
        emit({ type: "script.mismatch", data });
        return false;
      }
    }

    // 2b) cross-origin → observe default
    if (!/^https:\/\//i.test(src)) {
      const data: ScriptMismatchDetail = {
        key: src,
        src,
        inline: false,
        expected: undefined,
        got: "",
        ok: false,
        severity: "warn",
        reason: "insecure-http",
      };
      emit({ type: "script.mismatch", data });
      return false;
    }

    // CORS varsa hash’lemeyi deneyelim (nadir)
    let got = "";
    let fetched = false;
    try {
      const code = await (await fetch(src, { cache: "no-store" })).text();
      got = await sha256(code);
      fetched = true;
    } catch {}

    // Versioned CDN: tam URL pin’i
    const versionedPinned = man.versionedPins.has(src);
    // Dynamic loader: host/path/query pin eşleşiyor mu?
    const dynamicPinned = urlMatchesPin(src, man.dynamicPins);

    if (!versionedPinned && !dynamicPinned) {
      const data: ScriptMismatchDetail = {
        key: src,
        src,
        inline: false,
        expected: undefined,
        got: fetched ? got : "",
        ok: false,
        severity: "warn",
        reason: "url-pin-mismatch",
      };
      emit({ type: "script.mismatch", data });
      return false;
    }

    // Pin tuttu → observe OK
    emitCheckedVerbose(verbose, {
      key: src,
      src,
      inline: false,
      expected: undefined,
      got: fetched ? got : "",
      ok: true,
    });
    const data: ScriptOkDetail = {
      key: src,
      src,
      inline: false,
      expected: undefined,
      got: fetched ? got : "",
      ok: true,
      severity: "info",
    };
    emit({ type: "script.ok", data });
    return true;
  } catch (err: any) {
    emit({
      type: "error",
      data: { stage: "check", message: String(err?.message || err) },
    });
    return false;
  }
}

/** ---------- init ---------- **/
export async function initJSentinel(options: JSentinelOptions = {}) {
  const manifestPath = options.manifestPath ?? "/manifest.json";
  const watchClipboard = options.watchClipboard !== false;
  const verbose = !!options.verboseChecks;

  try {
    const r = await fetch(manifestPath, { cache: "no-store" });
    const raw = await r.json();

    const entries = Array.isArray(raw?.scripts) ? (raw.scripts as any[]) : [];
    const man = buildRuntimeManifest(entries);

    const scripts = Array.from(document.scripts) as HTMLScriptElement[];
    const results = await Promise.all(
      scripts.map((n) => check(n, man, verbose))
    );
    emit({
      type: "scan.complete",
      data: {
        total: results.length,
        ok: results.filter(Boolean).length,
        mismatched: results.filter((x) => !x).length,
      },
    });

    new MutationObserver((m) =>
      m
        .flatMap((x) => Array.from(x.addedNodes))
        .filter((n) => n.nodeName === "SCRIPT")
        .forEach((n) => {
          void check(n as HTMLScriptElement, man, verbose);
        })
    ).observe(document.documentElement, { childList: true, subtree: true });

    if (watchClipboard) {
      let selText = "";
      document.addEventListener("selectionchange", () => {
        selText = window.getSelection()?.toString() || selText;
      });
      document.addEventListener("copy", () => {
        const expected = selText;
        setTimeout(async () => {
          try {
            const clip = await navigator.clipboard.readText();
            if (expected && clip && clip !== expected) {
              emit({ type: "clipboard.tamper", data: { expected, got: clip } });
            }
          } catch {
            emit({ type: "clipboard.permission", data: {} });
          }
        }, 100);
      });
    }

    emit({ type: "ready", data: {} });
  } catch (e: any) {
    emit({
      type: "error",
      data: { stage: "init", message: String(e?.message || e) },
    });
  }
}

/** ---------- optional: console logger helper ---------- **/
export function attachConsoleLogger() {
  return onJSentinelAny((m) => {
    const ts = new Date().toISOString();
    if (m.type === "script.ok")
      console.info(`[JSentinel ${ts}] OK`, m.data.key);
    else if (m.type === "script.mismatch")
      console.warn(`[JSentinel ${ts}] MISMATCH`, m.data);
    else if (m.type === "scan.complete")
      console.log(`[JSentinel ${ts}] Scan`, m.data);
  });
}
